import { WHATSAPP_BUSINESS_INTEGRATION_URL } from '@/lib/whatsapp/businessAccountRoute';
import { roleRouteAllows, type RouteRole } from '@/lib/routeRoleAccess';

/**
 * Where clicking a bell notification takes the user — ONE resolver for the
 * bell dropdown and the Notifications page.
 *
 * 🔴 These used to be two hand-kept switches that had drifted apart: the page
 * had no case for tickets, conversation assignment, reminders or commerce
 * syncs, so the same row that opened its ticket from the dropdown bounced to
 * /home/notifications from the page. Add a destination HERE, never in a caller.
 *
 * Announcements (dialog) and contact-export downloads are side effects, not
 * destinations; callers handle those before asking for a target.
 */

export interface NotificationTarget {
  to: string;
  state?: Record<string, unknown>;
}

export const NOTIFICATIONS_PAGE_PATH = '/home/notifications';

// Billing inside Account settings (open to every role); /home/billing is the old UI.
const ACCOUNT_BILLING_PATH = '/home/account-settings?section=billing&interval=monthly';

const INBOX_TYPES = [
  'MESSAGE_RECEIVED',
  'MESSAGE_UNSUPPORTED',
  'chat_request',
  'customer_interaction',
  'cake_order_form',
  'cake_interest',
  'treats_interest',
  'order_confirmed',
  'session_ended',
];

const CAMPAIGN_TYPES = [
  'CAMPAIGN_CREATED',
  'CAMPAIGN_COMPLETED',
  'CAMPAIGN_COMPLETED_WITH_ERRORS',
  'CAMPAIGN_SCHEDULED',
  'CAMPAIGN_STARTED',
  'CAMPAIGN_RETRY_FAILED',
  'CAMPAIGN_LAUNCHED',
];

const TEMPLATE_TYPES = [
  'TEMPLATE_APPROVED',
  'TEMPLATE_REJECTED',
  'TEMPLATE_PAUSED',
  'TEMPLATE_DISABLED',
  'TEMPLATE_IN_APPEAL',
  'TEMPLATE_LIMIT_EXCEEDED',
  'TEMPLATE_PENDING_DELETION',
];

// Webhook-driven alerts about the WhatsApp number itself; the integration page
// is where its connection health and reconnect live.
const WHATSAPP_ACCOUNT_TYPES = [
  'WHATSAPP_ACCOUNT_ALERT',
  'WHATSAPP_SECURITY_EVENT',
  'WHATSAPP_ACCOUNT_REVIEW',
];

// Social inbox threads live on their own channel; `/home/inbox` always
// resolves to the WhatsApp channel (see InboxRedirect).
const SOCIAL_CHANNELS = ['instagram', 'facebook'];

/*
  Social-post results belong on the unified posts workspace, on the network's
  own tab. Rows written before the backend used `socialPostsDeepLink` point at
  the frozen per-network pages (/home/instagram/posts, /home/facebook/posts)
  or at the bare workspace, which opens the "All networks" tab.
*/
const SOCIAL_POSTS_PATH = '/home/social-media/posts';
const SOCIAL_POST_PLATFORMS = ['instagram', 'facebook', 'linkedin', 'tiktok', 'youtube'];
const LEGACY_SOCIAL_POSTS_PAGES: Record<string, string> = {
  '/home/instagram/posts': 'instagram',
  '/home/facebook/posts': 'facebook',
  '/home/linkedin/posts': 'linkedin',
  '/home/youtube/content': 'youtube',
  '/home/social-media/youtube/content': 'youtube',
};
const FAILED_POST_TYPES = ['SCHEDULED_POST_FAILED', 'SOCIAL_POST_PUBLISHED_FAILED', 'SOCIAL_STORY_PUBLISHED_FAILED'];

/** The posts-workspace link for a social-post deep link, or null for any other link. */
function socialPostsLink(deepLink: string, data: Record<string, unknown>, type: string): string | null {
  const [path, query = ''] = deepLink.split('#')[0].split('?');
  const legacyPlatform = LEGACY_SOCIAL_POSTS_PAGES[path];
  if (path !== SOCIAL_POSTS_PATH && !legacyPlatform) return null;

  const params = new URLSearchParams(query);
  if (!params.get('platform')) {
    const fromData = String(data.platform || '').toLowerCase();
    const platform =
      legacyPlatform ||
      (SOCIAL_POST_PLATFORMS.includes(fromData) ? fromData : '') ||
      (data.kind === 'instagram_publish' ? 'instagram' : '');
    if (platform) params.set('platform', platform);
  }
  if (FAILED_POST_TYPES.includes(type) && !params.get('status')) params.set('status', 'failed');
  const qs = params.toString();
  return qs ? `${SOCIAL_POSTS_PATH}?${qs}` : SOCIAL_POSTS_PATH;
}

/*
  Templates live on the Templates tab of the Campaigns workspace. Rows written
  before the backend used `templatesDeepLink` point at the old standalone
  /home/templates page. With a template id the tab opens that template's
  preview (NewTemplatesBoard matches `templateId` against the Meta id or name).
*/
const TEMPLATES_TAB_PATH = '/home/campaigns?tab=templates';

const templateRefOf = (data: Record<string, unknown>): string => {
  const ref = data.templateId || data.template_id || data.templateName;
  return ref ? String(ref).trim() : '';
};

const templatesTabLink = (data: Record<string, unknown>): string => {
  const ref = templateRefOf(data);
  return ref ? `${TEMPLATES_TAB_PATH}&templateId=${enc(ref)}` : TEMPLATES_TAB_PATH;
};

/** The Templates-tab link for a templates deep link, or null for any other link. */
function templatesLink(deepLink: string, data: Record<string, unknown>): string | null {
  const [path, query = ''] = deepLink.split('#')[0].split('?');
  const params = new URLSearchParams(query);
  const isLegacyPage = path.replace(/\/+$/, '') === '/home/templates';
  const isTemplatesTab = path === '/home/campaigns' && params.get('tab') === 'templates';
  if (!isLegacyPage && !isTemplatesTab) return null;

  const out = new URLSearchParams({ tab: 'templates' });
  params.forEach((value, key) => {
    if (key !== 'tab') out.set(key, value);
  });
  const ref = templateRefOf(data);
  if (ref && !out.get('templateId')) out.set('templateId', ref);
  return `/home/campaigns?${out.toString()}`;
}

/** '/x' only — '//host' is protocol-relative, i.e. external. */
export const isAppRelativePath = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

const enc = (value: unknown) => encodeURIComponent(String(value));

const conversationPath = (conversationId: unknown, platform: unknown) => {
  const channel = typeof platform === 'string' ? platform.toLowerCase() : '';
  const base = SOCIAL_CHANNELS.includes(channel) ? `/home/channels/${channel}` : '/home/inbox';
  return `${base}?conversationId=${enc(conversationId)}`;
};

/** The fields of a bell row the resolver reads. */
export interface NotificationLike {
  type?: string | null;
  message?: string | null;
  data?: unknown;
  [field: string]: unknown;
}

export function resolveNotificationTarget(notification: NotificationLike | null | undefined): NotificationTarget {
  const data: Record<string, unknown> =
    notification?.data && typeof notification.data === 'object'
      ? (notification.data as Record<string, unknown>)
      : {};
  const type: string = notification?.type || '';

  if (data?.kind === 'board_auto_send_failure' && typeof data.deep_link === 'string') {
    return { to: data.deep_link };
  }

  /*
    🔴 SYNC NOTIFICATIONS DO NOT USE `notification.type` FOR THEIR KIND — the
    backend stamps them with the generic "success" / "warning" / "error".
    Commerce syncs (`shopifySyncQueue.js`/`shopify-sync-worker.js`) carry the
    real kind in `data.type` ("products", "woo-orders", "checkouts", …);
    catalog syncs (`catalogSyncQueue.js`/`catalog-sync-worker.js`) carry only
    `data.provider`. WhatsApp template/form syncs carry a `deep_link` and fall
    through to it below.
  */
  if (type === 'success' || type === 'error' || type === 'warning') {
    const syncType = String(data?.type || '');
    if (syncType === 'products' || syncType === 'woo-products') {
      return { to: '/home/commerce?tab=products' };
    }
    if (syncType === 'orders' || syncType === 'woo-orders') {
      return { to: '/home/commerce?tab=orders' };
    }
    if (syncType === 'checkouts') {
      return { to: '/home/commerce?tab=carts' };
    }
    if (syncType === 'woo-coupons' || syncType === 'shopify-discounts') {
      return { to: '/home/commerce?tab=offers' };
    }
    // customers / petpooja-menu / shopify-webhooks have no tab of their
    // own — land on Commerce's default view rather than nowhere.
    if (syncType === 'customers' || syncType === 'petpooja-menu' || syncType === 'shopify-webhooks') {
      return { to: '/home/commerce' };
    }
    if (!syncType && data?.provider && !data?.deep_link) {
      // Catalog sync — started from Commerce's Products tab.
      return { to: '/home/commerce?tab=products' };
    }
  }

  if (type === 'DEAL_RENEWAL_REMINDER') {
    // The deals board (/home/deals) was removed; a deal now lives in its
    // lead's Deals tab. Rows written before the backend carried `contact_id`
    // still point at the dead route — send those to the CRM instead of a 404.
    const contactId = data.contact_id || data.contactId;
    return contactId
      ? { to: `/home/contacts?contactId=${enc(contactId)}&tab=deals` }
      : { to: '/home/boards/leads' };
  }

  if (isAppRelativePath(data?.deep_link)) {
    const agentId = data.agent_id || data.agentId;
    const userIp = data.user_ip || data.userIp || data.visitor_ip || data.visitorIp;
    return {
      to: socialPostsLink(data.deep_link, data, type) ?? templatesLink(data.deep_link, data) ?? data.deep_link,
      state: {
        agentId,
        selectedAiIp: userIp,
      },
    };
  }

  if (INBOX_TYPES.includes(type)) {
    const conversationId = data.conversationId || data.conversation_id;
    if (conversationId) {
      return {
        to: conversationPath(conversationId, data.platform),
        state: {
          selectedConversationId: conversationId,
          contactName: data.contactName || data.contact_name,
          targetMessageId: data.messageId || data.message_id || null,
        },
      };
    }
    const messageMatch = notification.message?.match(/^New message from (.+?):/);
    const contactName = data.contactName || data.contact_name || (messageMatch ? messageMatch[1] : null);
    return {
      to: '/home/inbox',
      state: {
        searchContactName: contactName,
      },
    };
  }

  if (CAMPAIGN_TYPES.includes(type)) {
    const campaignId = data.campaignId || data.campaign_id;
    return {
      to: campaignId
        ? `/home/campaigns?campaignId=${enc(campaignId)}`
        : '/home/campaigns',
    };
  }

  if (TEMPLATE_TYPES.includes(type)) {
    return { to: templatesTabLink(data) };
  }

  if (WHATSAPP_ACCOUNT_TYPES.includes(type)) {
    return { to: WHATSAPP_BUSINESS_INTEGRATION_URL };
  }

  switch (type) {
    case 'AI_AGENT_NEEDS_ATTENTION': {
      const agentId = data.agent_id || data.agentId;
      const userIp = data.user_ip || data.userIp || data.visitor_ip || data.visitorIp;
      let url = '/home/channels/ai-agents';
      if (agentId) {
        url += `?agentId=${enc(agentId)}`;
        if (userIp) {
          url += `&userIp=${enc(userIp)}`;
        }
      }
      return {
        to: url,
        state: {
          agentId,
          selectedAiIp: userIp,
        },
      };
    }
    case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
      // Nothing creates this today; if it returns, the number's status lives
      // on the WhatsApp Business integration, not the old /home/settings page.
      return { to: WHATSAPP_BUSINESS_INTEGRATION_URL };
    case 'TEAM_INVITATION':
      return { to: '/home/team-management' };
    case 'TICKET_ASSIGNED':
    case 'TICKET_COMMENT': {
      const ticketId = data.ticketId || data.ticket_id;
      return {
        to: ticketId ? `/home/tickets?ticketId=${enc(ticketId)}` : '/home/tickets',
      };
    }
    case 'CONVERSATION_ASSIGNED': {
      const convId = data.conversationId || data.conversation_id;
      if (!convId) return { to: '/home/inbox' };
      return {
        to: conversationPath(convId, data.platform),
        state: {
          selectedConversationId: convId,
          contactName: data.contactName || data.contact_name || null,
        },
      };
    }
    case 'REMINDER': {
      const conversationId = data.conversationId || data.conversation_id;
      const contactId = data.contactId || data.contact_id;
      if (conversationId) {
        return {
          to: `/home/inbox?conversationId=${enc(conversationId)}`,
          state: { selectedConversationId: conversationId },
        };
      }
      if (contactId) {
        return { to: `/home/contacts?contactId=${enc(contactId)}&tab=reminders` };
      }
      return { to: '/home/settings?tab=reminders' };
    }
    case 'SYSTEM_ALERT':
    case 'SYSTEM_INFO':
      // QualityProtectionService: campaigns auto-paused / auto-resumed.
      if (data.campaignsPausedCount !== undefined || data.campaignsResumedCount !== undefined) {
        return { to: '/home/campaigns' };
      }
      return { to: NOTIFICATIONS_PAGE_PATH };
    case 'CONTACT_IMPORT_COMPLETED':
    case 'CONTACT_IMPORT_FAILED':
      // Imported contacts land as CRM leads; /home/contacts is the old list.
      return { to: '/home/boards/leads' };
    case 'DATA_STORE_EXPORT_COMPLETED':
    case 'DATA_STORE_EXPORT_FAILED': {
      const storeId = data.storeId || data.store_id;
      return { to: storeId ? `/home/data-stores/${enc(storeId)}` : '/home/data-stores' };
    }
    case 'RIDER_ORDER_ASSIGNED':
      // CommerceController, on assigning a rider. The order lives in
      // Commerce → Orders (no per-order link exists); that page is admin-only,
      // so a rider gets the no-access notice — riders work orders in the app.
      return { to: '/home/commerce?tab=orders' };
    case 'SUBSCRIPTION_REMINDER': {
      // Rows written before the backend moved off it still carry the old
      // standalone /home/billing page as their actionUrl.
      const actionUrl = isAppRelativePath(data.actionUrl) ? data.actionUrl : null;
      const isLegacyBilling = actionUrl !== null && actionUrl.split(/[?#]/)[0].replace(/\/+$/, '') === '/home/billing';
      return { to: actionUrl && !isLegacyBilling ? actionUrl : ACCOUNT_BILLING_PATH };
    }
    default:
      return { to: NOTIFICATIONS_PAGE_PATH };
  }
}

/**
 * The `<RoleRoute allowed>` of every page a notification can open, keyed by
 * its App.tsx path under /home. A test pins each entry to App.tsx. A path not
 * listed here is left to the route itself.
 *
 * 🔴 A role a route does not allow is sent to /home/inbox by RoleRoute with no
 * explanation — which is what an AGENT clicking a board or ads notification
 * used to get. Checked up front, the click can say why instead.
 */
export const NOTIFICATION_ROUTE_ROLES: Record<string, readonly RouteRole[]> = {
  campaigns: ['ADMIN', 'USER'],
  templates: ['ADMIN', 'USER'],
  contacts: ['ADMIN', 'USER'],
  'team-management': ['ADMIN'],
  commerce: ['ADMIN'],
  'data-stores': ['ADMIN'],
  'data-stores/:id': ['ADMIN'],
  'integrations/whatsapp-business': ['ADMIN'],
  billing: ['ADMIN'],
  plans: ['ADMIN'],
  'boards/leads': ['ADMIN', 'USER', 'AGENT'],
  'boards/:id': ['ADMIN', 'USER'],
  ads: ['ADMIN'],
  'ads/attribution': ['ADMIN'],
  flows: ['ADMIN'],
  'instagram/posts': ['ADMIN'],
  'facebook/posts': ['ADMIN'],
  'social-media/posts': ['ADMIN'],
};

// Legacy paths that redirect to a gated page without a gate of their own.
const ROUTE_ALIASES: Record<string, string> = {
  'business-accounts': 'integrations/whatsapp-business',
};

/** The allowed roles for an app path, or null when notifications don't gate it. */
export function notificationRouteRoles(to: string): readonly RouteRole[] | null {
  const pathname = to.split(/[?#]/)[0];
  if (!pathname.startsWith('/home/')) return null;
  const rel = pathname.slice('/home/'.length).replace(/\/+$/, '');
  const key = ROUTE_ALIASES[rel] ?? rel;
  if (NOTIFICATION_ROUTE_ROLES[key]) return NOTIFICATION_ROUTE_ROLES[key];

  const segments = key.split('/');
  for (const [route, allowed] of Object.entries(NOTIFICATION_ROUTE_ROLES)) {
    const parts = route.split('/');
    if (!parts.includes(':id') || parts.length !== segments.length) continue;
    if (parts.every((part, i) => part === ':id' || part === segments[i])) return allowed;
  }
  return null;
}

export const NOTIFICATION_ACCESS_DENIED_TOAST = {
  title: "You don't have access to this page",
  description: 'This notification opens a page your role can’t view. Ask a workspace admin for access.',
};

/** False only when the route gate is known to turn this user away. */
export function canOpenNotificationTarget(user: unknown, to: string): boolean {
  if (!user) return true;
  const allowed = notificationRouteRoles(to);
  if (!allowed) return true;
  return roleRouteAllows(user, allowed, to.split(/[?#]/)[0]);
}
